import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import {
  buildSanctionsQueueView,
  type SanctionsQueueView,
} from './sanctionsQueueModel';
import type { Market, SanctionsMatch, User } from '../dataProvider/types';

/**
 * The sanctions-screening queue, read through the aggregation seam (`buildSanctionsQueueView`),
 * NOT computed in the component. For v1 it pulls the engine-written matches and users from the
 * mock provider and aggregates client-side; the real-data migration replaces the
 * fetch-and-aggregate with a server-side endpoint and the surface does not change. Same seam idiom
 * as useDsarQueue / useSuspensions.
 *
 * OVERSIGHT-GLOBAL: it does NOT read the region switcher. It is global by default; the optional
 * `regionFilter` (the surface's own facet) narrows by the match's market by exact value.
 * `refreshKey` lets the surface force a refetch after a clear / confirm so the lists and the
 * derived standing reflect the new state.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useSanctionsQueue = (
  regionFilter: Market | null,
  refreshKey = 0,
): { view: SanctionsQueueView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['sanctions-queue', regionFilter, refreshKey],
    queryFn: async () => {
      const [sanctionsMatches, users] = await Promise.all([
        dataProvider.getList('sanctionsMatches', ALL),
        dataProvider.getList('users', ALL),
      ]);
      return buildSanctionsQueueView(
        {
          sanctionsMatches:
            sanctionsMatches.data as unknown as SanctionsMatch[],
          users: users.data as unknown as User[],
        },
        { regionFilter },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
